import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";

const BatchResultModal = ({ isOpen, onClose, result }) => {
  const [copied, setCopied] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const loaRows = result?.loaRows || [];
  const totalPages = Math.max(1, Math.ceil(loaRows.length / pageSize));
  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return loaRows.slice(start, start + pageSize);
  }, [loaRows, page]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(1);
    }
  }, [page, totalPages]);

  useEffect(() => {
    setPage(1);
    setCopied(false);
  }, [result]);

  const handleCopy = async () => {
    if (!result?.batchNo) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(result.batchNo);
      } else {
        const el = document.createElement("textarea");
        el.value = result.batchNo;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (err) {
      setCopied(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6 backdrop-blur-sm"
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.96, opacity: 0 }}
          className="w-full max-w-4xl rounded-2xl border border-slate-100 bg-white p-6 shadow-2xl max-h-[92vh] overflow-y-auto"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">
                Batch Result
              </p>
              <h2 className="text-2xl font-semibold text-slate-900">
                {result?.success ? "Batch Created" : "Batch Creation Failed"}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 transition"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm text-slate-700">
              {result?.message || "Request completed."}
            </p>
          </div>

          {result?.success && (
            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">
                Newly Created Batch Number
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <span className="text-lg font-semibold text-slate-900">
                  {result?.batchNo || "-"}
                </span>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
                >
                  <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          )}

          {result?.success ? (
            <div className="mt-6 rounded-xl border border-slate-200">
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <p className="text-sm font-semibold text-slate-700">
                  LOA List ({loaRows.length})
                </p>
                <p className="text-xs text-slate-500">
                  Page {page} of {totalPages}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-4 py-3">LOA#</th>
                      <th className="px-4 py-3">Coverage</th>
                      <th className="px-4 py-3">Date Received</th>
                      <th className="px-4 py-3">Received By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-4 py-6 text-center text-sm text-slate-500"
                        >
                          No LOA records available.
                        </td>
                      </tr>
                    ) : (
                      pagedRows.map((row) => (
                        <tr
                          key={row.loaNo}
                          className="border-t border-slate-100"
                        >
                          <td className="px-4 py-3 font-semibold text-slate-800">
                            {row.loaNo}
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {row.coverageCode}
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {row.dateReceived}
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {row.receivedBy}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-end gap-2 px-4 py-3">
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={page === 1}
                  className="rounded-md border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={page === totalPages}
                  className="rounded-md border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-6 rounded-xl border border-red-200">
              <div className="border-b border-red-100 bg-red-50 px-4 py-3">
                <p className="text-sm font-semibold text-red-700">
                  Already Batched LOA
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-red-50 text-xs uppercase text-red-500">
                    <tr>
                      <th className="px-4 py-3">LOA#</th>
                      <th className="px-4 py-3">Batch No</th>
                      <th className="px-4 py-3">Batched By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(result?.failed || []).length === 0 ? (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-4 py-6 text-center text-sm text-red-500"
                        >
                          No details available.
                        </td>
                      </tr>
                    ) : (
                      result.failed.map((item, index) => (
                        <tr key={`${item.loa_no}-${index}`} className="border-t">
                          <td className="px-4 py-3 text-slate-700">
                            {item.loa_no}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {item.batch_no || "-"}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {item.batched_by || "-"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BatchResultModal;
